import React from "react";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppTitle, AppParagraph } from "@/shared/Components";
import { ContactCard } from "@/common";
import { CancelButton, SaveButton } from "@/shared/Components/Buttons";
import { useAppDispatch } from "@/services/store";
import { appRouterPaths } from "@/services/navigation";
import { useRuntimeImage } from "@/entities/runtime-image";
import { commitmentsPageActions } from "@/pages/CommitmentsPage";
import { handleFullName } from "@/shared/utils";
import type { Note, Person } from "@/services/api";
import styles from "./styles.module.scss";

interface Props {
  person?: Person | null;
  title: string;
  describe: string;
  note?: Note | null;
}

export const CalendarEventTooltip: React.FC<Props> = ({ person, title, describe, note }) => {
  const { t } = useTranslation();
  const { getRuntimeImageURL } = useRuntimeImage();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const onClickToNote = () => {
    if (note) {
      navigate(appRouterPaths.commitments);
      dispatch(commitmentsPageActions.openNoteById({ noteId: note.id }));
    }
  };

  return (
    <div className={styles["tooltip-container"]}>
      <ContactCard
        contact={{
          email: person?.emails[0]?.email ?? "",
          id: person?.id ?? "",
          image: getRuntimeImageURL({ fileId: person?.avatarFile?.fileId ?? "" }),
          name: handleFullName(person?.firstName, person?.lastName),
          phone: person?.phones[0]?.number ?? "",
          title: person?.careers[person?.careers?.length - 1]?.jobTitle ?? "",
        }}
        className={styles["calendar-tooltip-contact-card"]}
      />
      <div className={styles["body-container"]}>
        <AppTitle size="lg" titleType="primary" lineHeight="xxl" className={styles.title}>
          {title}
        </AppTitle>
        <AppParagraph textType="primary" lineHeight="lg">
          {describe}
        </AppParagraph>
      </div>
      <div className={styles["button-container"]}>
        <CancelButton
          onClick={() => navigate(`/contacts/generalInfo/${person?.id}`)}
          className={classNames(styles.button, styles["cancel-btn"])}
        >
          {t("calendarPage.toContact")}
        </CancelButton>
        <SaveButton onClick={onClickToNote} className={styles.button}>
          {t("calendarPage.toNote")}
        </SaveButton>
      </div>
    </div>
  );
};
